/* ONNI · Linien-Switcher CLASSIC / SENSITIVE (Johannes 12.10.2026, Ada)
 *
 * Wechselt zwischen zwei getrennten Produkten ohne vollen Seiten-Reload:
 * Partnerseite vorladen, <main> per morph austauschen, URL per pushState setzen.
 * Das Theme macht es bei verbundenen Produkten genauso (variant-picker.js, updateMain).
 * Bei jedem Fehler fällt der Klick auf einen normalen Seitenwechsel zurück.
 */
import { morph } from '@theme/morph';

const cache = new Map();
let gewechselt = false;
let angezeigt = location.href;

/** @param {string} url */
function laden(url) {
  if (!cache.has(url)) {
    const anfrage = fetch(url, { credentials: 'same-origin' }).then((antwort) => {
      if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
      return antwort.text();
    });
    anfrage.catch(() => cache.delete(url));
    cache.set(url, anfrage);
  }
  return cache.get(url);
}

/** @param {Element} wurzel */
function vorladen(wurzel) {
  wurzel.querySelectorAll('a[data-partner]').forEach((link) => {
    if (link instanceof HTMLAnchorElement) laden(link.href).catch(() => {});
  });
}

/** @param {string} url */
async function wechseln(url) {
  const html = await laden(url);
  const neu = new DOMParser().parseFromString(html, 'text/html');
  neu.querySelector('overflow-list[defer]')?.removeAttribute('defer');
  const main = document.querySelector('main');
  const neuesMain = neu.querySelector('main');
  if (!main || !neuesMain || !neuesMain.querySelector('onni-linien-switch')) throw new Error('kein main');

  const scrollY = window.scrollY;
  morph(main, neuesMain);
  // Codepreis sofort im selben Durchlauf setzen, sonst blitzt der Normalpreis auf,
  // bis der Observer in discount.js nach 250 ms nachzieht (Johannes 12.10.2026)
  document.dispatchEvent(new CustomEvent('onni:variant-swapped', { detail: { section: main } }));
  document.title = neu.title;
  history.pushState({ onniLinienSwitch: true }, '', url);
  gewechselt = true;
  angezeigt = location.href;
  window.scrollTo({ top: scrollY, left: window.scrollX, behavior: 'instant' });

  // Seitenaufruf für Analytics melden, Shopify selbst sieht nur den ersten Aufruf
  const daten = /** @type {any} */ (window).dataLayer;
  if (Array.isArray(daten)) daten.push({ event: 'onni_produkt_gewechselt', page_location: location.href, page_title: document.title });
  document.dispatchEvent(new CustomEvent('onni:produkt-gewechselt', { detail: { url } }));
}

class OnniLinienSwitch extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#klick);
    const leerlauf = /** @type {any} */ (window).requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    leerlauf(() => vorladen(this));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#klick);
  }

  /** @param {MouseEvent} event */
  #klick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (link.classList.contains('is-on') || this.getAttribute('aria-busy') === 'true') return;

    this.setAttribute('aria-busy', 'true');
    wechseln(link.href)
      .catch(() => {
        window.location.href = link.href;
      })
      .finally(() => {
        document.querySelectorAll('onni-linien-switch[aria-busy]').forEach((el) => el.removeAttribute('aria-busy'));
        document.querySelectorAll('onni-linien-switch').forEach(vorladen);
      });
  };
}

if (!customElements.get('onni-linien-switch')) customElements.define('onni-linien-switch', OnniLinienSwitch);

// Zurück-Knopf nach einem Wechsel: die passende Seite sauber neu laden.
// Nur wenn sich die Adresse wirklich ändert: Am Handy legt der Warenkorb beim
// Öffnen einen eigenen Verlaufseintrag an und geht beim Schließen per
// history.back() zurück. Ohne diese Prüfung lud das die Seite neu (19.10.2026).
window.addEventListener('popstate', () => {
  if (gewechselt && location.href !== angezeigt) window.location.reload();
});
